/**
 * @file Cascader 级联选择
 * @description 多级面板选择；值路径 string[]；Portal + fade + 视口边界。
 *
 * @whenToUse 省市区、类目、组织路径等层级枚举。
 * @whenNotToUse 扁平搜索单选（Combobox）；树形单面板（TreeSelect）。
 */

import { useEffect, useId, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Check, ChevronRight, ChevronsUpDown } from 'lucide-react'
import { cn } from '@/lib/cn.ts'
import {
  computeDropdownBox,
  floatingPanelStyle,
  type FloatingBox,
} from '@/lib/floating.ts'
import { ScrollArea } from '@/components/ui/scroll-area.tsx'
import {
  searchControlWidth,
  useSearchForm,
} from '@/components/ui/search-form-context.tsx'

export type CascaderOption = {
  value: string
  label: ReactNode
  disabled?: boolean
  children?: CascaderOption[]
}

export type CascaderProps = {
  options: CascaderOption[]
  /** 选中路径，如 ['zhejiang', 'hangzhou'] */
  value?: string[]
  onChange?: (value: string[], selectedOptions: CascaderOption[]) => void
  placeholder?: string
  disabled?: boolean
  invalid?: boolean
  className?: string
  id?: string
}

function findPathLabels(
  options: CascaderOption[],
  path: string[],
): ReactNode[] {
  const labels: ReactNode[] = []
  let level = options
  for (const key of path) {
    const hit = level.find((item) => item.value === key)
    if (!hit) break
    labels.push(hit.label)
    level = hit.children ?? []
  }
  return labels
}

function findPathOptions(
  options: CascaderOption[],
  path: string[],
): CascaderOption[] {
  const selected: CascaderOption[] = []
  let level = options
  for (const key of path) {
    const hit = level.find((item) => item.value === key)
    if (!hit) break
    selected.push(hit)
    level = hit.children ?? []
  }
  return selected
}

export function Cascader({
  options,
  value = [],
  onChange,
  placeholder = '请选择',
  disabled = false,
  invalid = false,
  className,
  id,
}: CascaderProps) {
  const inSearchForm = useSearchForm()
  const panelId = useId()
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [visible, setVisible] = useState(false)
  const [box, setBox] = useState<FloatingBox | null>(null)
  const [activePath, setActivePath] = useState<string[]>(value)

  useEffect(() => {
    if (open) setActivePath(value)
  }, [open, value])

  const labels = findPathLabels(options, value)

  const columns: CascaderOption[][] = []
  let levelOptions = options
  columns.push(levelOptions)
  for (const key of activePath) {
    const hit = levelOptions.find((item) => item.value === key)
    if (!hit?.children?.length) break
    levelOptions = hit.children
    columns.push(levelOptions)
  }

  const updateBox = () => {
    const el = triggerRef.current
    if (!el) return
    setBox(computeDropdownBox(el.getBoundingClientRect(), 4, 280))
  }

  useEffect(() => {
    if (open) {
      setMounted(true)
      updateBox()
      const frame = requestAnimationFrame(() => setVisible(true))
      return () => cancelAnimationFrame(frame)
    }
    setVisible(false)
    const timer = window.setTimeout(() => setMounted(false), 200)
    return () => window.clearTimeout(timer)
  }, [open])

  useEffect(() => {
    if (!open) return
    const onWin = () => updateBox()
    window.addEventListener('resize', onWin)
    window.addEventListener('scroll', onWin, true)
    return () => {
      window.removeEventListener('resize', onWin)
      window.removeEventListener('scroll', onWin, true)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const onPointer = (event: MouseEvent) => {
      const t = event.target as Node
      if (triggerRef.current?.contains(t)) return
      if (panelRef.current?.contains(t)) return
      setOpen(false)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const handlePick = (columnIndex: number, option: CascaderOption) => {
    if (option.disabled) return
    const next = [...activePath.slice(0, columnIndex), option.value]
    setActivePath(next)
    if (!option.children?.length) {
      onChange?.(next, findPathOptions(options, next))
      setOpen(false)
    }
  }

  return (
    <>
      <button
        id={id}
        ref={triggerRef}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={panelId}
        className={cn(
          'inline-flex h-8 items-center justify-between gap-1.5 rounded-lg border bg-raised px-2 text-left text-sm transition-opacity duration-200 disabled:cursor-not-allowed disabled:opacity-50',
          searchControlWidth(inSearchForm),
          invalid ? 'border-danger' : 'border-border',
          className,
        )}
        onClick={() => setOpen((v) => !v)}
      >
        <span
          className={cn(
            'min-w-0 flex-1 truncate',
            labels.length ? 'text-foreground' : 'text-muted',
          )}
        >
          {labels.length
            ? labels.map((label, i) => (
                <span key={i}>
                  {i > 0 ? ' / ' : null}
                  {label}
                </span>
              ))
            : placeholder}
        </span>
        <ChevronsUpDown className="size-4 shrink-0 text-muted" aria-hidden />
      </button>

      {mounted && box && typeof document !== 'undefined'
        ? createPortal(
            <div
              ref={panelRef}
              id={panelId}
              className={cn(
                'fixed z-50 overflow-hidden rounded-lg border border-border bg-raised transition-opacity duration-200',
                visible ? 'opacity-100' : 'opacity-0',
              )}
              style={{
                ...floatingPanelStyle(box),
                width: Math.min(
                  window.innerWidth - 16,
                  Math.max(box.width, columns.length * 140),
                ),
              }}
            >
              <div className="flex" style={{ maxHeight: box.maxHeight }}>
                {columns.map((column, columnIndex) => (
                  <ScrollArea
                    key={columnIndex}
                    className={cn(
                      'min-w-36 flex-1',
                      columnIndex < columns.length - 1 &&
                        'border-r border-border',
                    )}
                    style={{ height: Math.min(box.maxHeight, 240) }}
                  >
                    <div className="p-1">
                      {column.map((option) => {
                        const active = activePath[columnIndex] === option.value
                        const isLeaf = !option.children?.length
                        return (
                          <button
                            key={option.value}
                            type="button"
                            disabled={option.disabled}
                            className={cn(
                              'flex h-8 w-full items-center justify-between gap-2 rounded-md px-2 text-left text-sm transition-opacity duration-200 hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50',
                              active ? 'bg-primary/15 text-primary' : 'text-foreground',
                            )}
                            onClick={() => handlePick(columnIndex, option)}
                          >
                            <span className="min-w-0 truncate">{option.label}</span>
                            {isLeaf && active ? (
                              <Check className="size-3.5 shrink-0" aria-hidden />
                            ) : !isLeaf ? (
                              <ChevronRight
                                className="size-3.5 shrink-0 text-muted"
                                aria-hidden
                              />
                            ) : null}
                          </button>
                        )
                      })}
                    </div>
                  </ScrollArea>
                ))}
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  )
}
